import {
  AlertCircle,
  CheckCircle,
  Clock,
  Info,
  Pause,
  Play,
  RotateCcw,
  User,
  XCircle,
} from 'lucide-react'
import { useMemo } from 'react'

import { getBrowserTimezoneDatetimeString } from 'utils'
import { type BrokersWorkflow } from 'utils/web_api.schemas'

type WorkflowStatusMetadataProps = {
  workflow: BrokersWorkflow
}

type MetadataField = {
  label: string
  key: string
  icon: React.ComponentType<{ className?: string }>
  type: 'datetime' | 'text' | 'user'
}

// Map workflow statuses to their relevant metadata fields
const STATUS_METADATA_FIELDS: Record<string, MetadataField[]> = {
  paused: [
    {
      label: 'Paused At',
      key: 'paused_at',
      icon: Clock,
      type: 'datetime',
    },
    {
      label: 'Paused By',
      key: 'paused_by',
      icon: User,
      type: 'user',
    },
    {
      label: 'Pause Reason',
      key: 'pause_reason',
      icon: Pause,
      type: 'text',
    },
  ],
  in_progress: [
    {
      label: 'Started At',
      key: 'started_at',
      icon: Clock,
      type: 'datetime',
    },
    {
      label: 'Started By',
      key: 'started_by',
      icon: User,
      type: 'user',
    },
    {
      label: 'Start Reason',
      key: 'start_reason',
      icon: Play,
      type: 'text',
    },
  ],
  completed: [
    {
      label: 'Completed At',
      key: 'completed_at',
      icon: Clock,
      type: 'datetime',
    },
    {
      label: 'Completed By',
      key: 'completed_by',
      icon: User,
      type: 'user',
    },
    {
      label: 'Completion Reason',
      key: 'completion_reason',
      icon: CheckCircle,
      type: 'text',
    },
  ],
  canceled: [
    {
      label: 'Canceled At',
      key: 'canceled_at',
      icon: Clock,
      type: 'datetime',
    },
    {
      label: 'Canceled By',
      key: 'canceled_by',
      icon: User,
      type: 'user',
    },
    {
      label: 'Cancellation Reason',
      key: 'cancellation_reason',
      icon: XCircle,
      type: 'text',
    },
  ],
  rolled_back: [
    {
      label: 'Rolled Back At',
      key: 'rolled_back_at',
      icon: Clock,
      type: 'datetime',
    },
    {
      label: 'Rolled Back By',
      key: 'rolled_back_by',
      icon: User,
      type: 'user',
    },
    {
      label: 'Rollback Reason',
      key: 'rollback_reason',
      icon: RotateCcw,
      type: 'text',
    },
  ],
  failed: [
    {
      label: 'Failed At',
      key: 'transition_failure_at',
      icon: Clock,
      type: 'datetime',
    },
    {
      label: 'Failure Reason',
      key: 'failure_reason',
      icon: AlertCircle,
      type: 'text',
    },
    {
      label: 'Failed Task',
      key: 'failed_task_class',
      icon: Info,
      type: 'text',
    },
  ],
}

const WorkflowStatusMetadata = ({ workflow }: WorkflowStatusMetadataProps) => {
  if (!workflow.metadata || !workflow.status) return null

  const metadata = workflow.metadata as Record<string, unknown>
  const fields = STATUS_METADATA_FIELDS[workflow.status] || []

  // Filter to only show fields that have values
  const visibleFields = useMemo(() => {
    return fields.filter((field) => {
      const value = metadata[field.key]
      return value !== undefined && value !== null && value !== ''
    })
  }, [fields, metadata])

  if (visibleFields.length === 0) return null

  const formatValue = (field: MetadataField, value: unknown): string => {
    if (value === null || value === undefined) return 'N/A'

    switch (field.type) {
      case 'datetime':
        try {
          return getBrowserTimezoneDatetimeString(new Date(value as string))
        } catch {
          return String(value)
        }
      case 'user':
        // TODO: Handle user IDs - you may want to fetch actual user names
        return String(value)
      case 'text':
      default:
        return String(value)
    }
  }

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'paused':
        return 'text-amber-600'
      case 'in_progress':
        return 'text-blue-600'
      case 'completed':
        return 'text-green-600'
      case 'canceled':
        return 'text-gray-600'
      case 'failed':
        return 'text-red-600'
      case 'rolled_back':
        return 'text-purple-600'
      default:
        return 'text-gray-600'
    }
  }

  const statusColor = getStatusColor(workflow.status)

  return (
    <div className='flex-1 space-y-2'>
      <h6 className='font-medium text-sm text-gray-600'>Status Information:</h6>
      <div className='space-y-1.5'>
        {visibleFields.map((field) => {
          const Icon = field.icon
          const value = metadata[field.key]

          return (
            <div key={field.key} className='flex items-center space-x-2 text-sm'>
              <Icon className={`w-4 h-4 flex-shrink-0 ${statusColor}`} />
              <div className='flex-1 min-w-0'>
                <span className='font-medium text-gray-700'>{field.label}: </span>
                <span className='text-gray-600'>{formatValue(field, value)}</span>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default WorkflowStatusMetadata
